export default function ProofSection() {
  return (
    <section id="proof" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="bg-white rounded-xl shadow-2xl p-8 md:p-12 border border-gray-200 mb-0">

          <div className="text-center mb-4">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
              私の過去の1日を、事例として紹介します。
            </h3>
          </div>

          <div className="space-y-12 mb-10">
            {/* ① 毎朝7時、同じ1日が始まる */}
            <div className="text-center max-w-4xl mx-auto bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="mb-8">
                <div className="relative w-full max-w-xl mx-auto" style={{ paddingBottom: '56.25%' }}>
                  <img
                    src="https://public.readdy.ai/ai/img_res/e64fbd682117928a43310607e11ab2c4.jpg"
                    alt="朝7時に起きる"
                    className="absolute inset-0 w-full h-full object-cover object-top rounded-lg shadow-md"
                  />
                </div>
              </div>
              <div className="text-left space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed">
                <p>
                  平日の朝7時。<br />
                  目覚ましの音に叩き起こされるように目を覚ます。<br />
                  まだ眠気が残る中、妻が用意してくれた朝食を口に運ぶ。
                </p>
                <p>
                  子どもと二言三言、言葉を交わすが、<br />
                  会話というより、作業に近い。<br />
                  時計ばかりを気にして、慌ただしく家を出る。
                </p>
                <p>
                  最寄り駅に着くころには、もう気が重い。<br />
                  ホームには人、人、人。<br />
                  列に並んでいるだけなのに、すでに疲れている。
                </p>
                <p>
                  電車が来る。<br />
                  乗り込むというより、押し込まれる。<br />
                  背中と背中、肩と肩が密着し、身動きが取れない。<br />
                  吊り革にすら手が届かず、ただ揺られるだけ。
                </p>
                <p>
                  「これがあと何十年続くんだろう」<br />
                  そんな考えが頭をよぎるが、答えは出ない。
                </p>
                <p>
                  ぐったりしたまま会社に到着する。
                </p>
                <p>
                  朝から部下の愚痴を聞き、<br />
                  終わりの見えない会議に参加し、<br />
                  責任がのしかかる重い指示を出す。<br />
                  自分の仕事に取りかかるころには、もう昼が近い。
                </p>
                <p>
                  ランチは1300円。<br />
                  高いと思いながらも、部下のやる気を出すためにも食事を奢る。
                </p>
                <p>
                  財布を開くたび、<br />
                  手元のお金が確実に減っていくのが分かる。<br />
                  しかも、自由も、余裕も、増えていない。
                </p>
                <p>
                  「何のために働いているんだろう」<br />
                  そう思いながら、午後もまた仕事に戻る。
                </p>
              </div>
            </div>

            {/* ② 働いても、余裕は増えない */}
            <div className="text-center max-w-4xl mx-auto bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="mb-8">
                <div className="relative w-full max-w-xl mx-auto" style={{ paddingBottom: '56.25%' }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=Evocative%20illustration%20of%20exhausted%20Japanese%20businessman%20commuting%20on%20crowded%20Tokyo%20train%20during%20evening%20rush%20hour%2C%20showing%20emotional%20fatigue%20and%20mental%20exhaustion%20through%20body%20language%2C%20contemporary%20artistic%20style%20with%20cohesive%20color%20scheme%20featuring%20warm%20neutral%20palette%20of%20soft%20beige%20cream%20light%20gray%20and%20muted%20earth%20tones%2C%20atmospheric%20lighting%20creating%20subdued%20melancholic%20mood&width=800&height=450&seq=commute-train-unified-002&orientation=landscape"
                    alt="満員電車での帰宅"
                    className="absolute inset-0 w-full h-full object-cover object-top rounded-lg shadow-md"
                  />
                </div>
              </div>
              <div className="text-left space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed">
                <p>
                  夜まで働き、<br />
                  部下のマネジメントや気遣いに神経をすり減らしたまま、<br />
                  気づけば一日が終わりそう。
                </p>
                <p>
                  定時はとっくに過ぎている。<br />
                  部下からの確認、上からの「今日中で」という指示。<br />
                  やることだけが増えていく。
                </p>
                <p>
                  ようやくパソコンを閉じた頃には、外は真っ暗だ。
                </p>
                <p>
                  1時間半かけて帰宅する頃には、<br />
                  体も気持ちもヘトヘトだった。
                </p>
                <p>
                  家に帰ると、<br />
                  子どもはもう寝ている。<br />
                  見られるのは、静かな寝顔だけ。
                </p>
                <p>
                  「お風呂、沸いてるから早く入って」<br />
                  「ガス代もったいないから」
                </p>
                <p>
                  分かっている。<br />
                  それでも、胸が少し苦しくなる。
                </p>
                <p>
                  一人でレンジで温めたご飯を食べる。<br />
                  テレビの音だけが流れる部屋で、<br />
                  自分の居場所が分からなくなる。
                </p>
                <p className="font-bold text-red-600">
                  ――正直、寂しかった。
                </p>
              </div>
            </div>

            {/* ③ 休みの日ですら、休めない */}
            <div className="text-center max-w-4xl mx-auto bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="mb-8">
                <div className="relative w-full max-w-xl mx-auto" style={{ paddingBottom: '56.25%' }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=Tender%20illustration%20of%20tired%20Japanese%20father%20being%20woken%20up%20by%20young%20child%20on%20Saturday%20morning%20in%20modern%20Japanese%20home%2C%20showing%20the%20struggle%20between%20exhaustion%20and%20parental%20love%2C%20warm%20family%20scene%20rendered%20in%20soft%20minimalist%20style%20with%20harmonious%20color%20palette%20of%20gentle%20beige%20cream%20light%20gray%20and%20warm%20neutral%20tones%2C%20natural%20morning%20light%20creating%20intimate%20domestic%20atmosphere&width=800&height=450&seq=weekend-rest-unified-002&orientation=landscape"
                    alt="休日も休めない"
                    className="absolute inset-0 w-full h-full object-cover object-top rounded-lg shadow-md"
                  />
                </div>
              </div>
              <div className="text-left space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed">
                <p>
                  やっと迎えた土曜日。<br />
                </p>
                <p>
                  疲れ切って寝だめをしようとすると、<br />
                  「パパ、遊ぼうよ」と言われる。
                </p>
                <p>
                  妻はパートへ。<br />
                  代わりに家事をこなす。
                </p>
                <p className="font-bold text-gray-900">
                  誰も悪くない。<br />
                  でも、余裕がなく心が休まらない。
                </p>
              </div>
            </div>

            {/* ④ 月に一度の給料日 */}
            <div className="text-center max-w-4xl mx-auto bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="mb-8">
                <div className="relative w-full max-w-xl mx-auto" style={{ paddingBottom: '56.25%' }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=Melancholic%20illustration%20of%20Japanese%20salaryman%20in%20business%20suit%20looking%20disappointed%20and%20sad%20while%20examining%20his%20salary%20statement%20paper%20in%20modern%20home%20office%2C%20showing%20emotional%20distress%20about%20deductions%20and%20take-home%20pay%2C%20contemporary%20artistic%20style%20with%20unified%20warm%20color%20palette%20of%20soft%20beige%20cream%20light%20gray%20and%20muted%20neutral%20earth%20tones%2C%20subdued%20natural%20lighting%20creating%20somber%20contemplative%20atmosphere%20about%20financial%20reality%20and%20monthly%20income%20concerns&width=800&height=450&seq=salary-sadness-illustration-001&orientation=landscape"
                    alt="給料日"
                    className="absolute inset-0 w-full h-full object-cover object-top rounded-lg shadow-md"
                  />
                </div>
              </div>
              <div className="text-left space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed">
                <p>
                  待ちに待った給料日。
                </p>
                <p>
                  給料明細を見る。<br />
                  額面は62万円。<br />
                  年収は、約1,000万円。
                </p>
                <p className="font-bold text-red-600">
                  でも、<br />
                  口座に振り込まれていたのは 46万円。
                </p>
                <p>
                  その金額を妻に伝える。
                </p>
                <p>
                  「お疲れ様」
                </p>
                <p className="font-bold text-gray-900">
                  その一言に、少し救われる。<br />
                  この瞬間だけは、<br />
                  自分が家族の役に立てている気がした。
                </p>
              </div>
            </div>

            {/* ⑤ それでも「足りない」と言われる現実 */}
            <div className="text-center max-w-4xl mx-auto bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="mb-8">
                <div className="relative w-full max-w-xl mx-auto" style={{ paddingBottom: '56.25%' }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=Tense%20illustration%20of%20Japanese%20couple%20having%20difficult%20conversation%20about%20household%20finances%20at%20dining%20table%20with%20bills%20and%20budget%20papers%2C%20showing%20stress%20and%20concern%20on%20their%20faces%2C%20modern%20home%20interior%20rendered%20in%20unified%20minimalist%20style%20with%20warm%20color%20scheme%20of%20soft%20beige%20cream%20light%20gray%20and%20muted%20neutral%20tones%2C%20subdued%20evening%20lighting%20creating%20serious%20atmosphere&width=800&height=450&seq=financial-stress-talk-unified-003&orientation=landscape"
                    alt="足りないと言われる"
                    className="absolute inset-0 w-full h-full object-cover object-top rounded-lg shadow-md"
                  />
                </div>
              </div>
              <div className="text-left space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed">
                <p>
                  でも、すぐに続く言葉。
                </p>
                <p>
                  「これじゃ足りないよね」<br />
                  「子どもの教育費もあるし」
                </p>
                <p>
                  責められているわけじゃない。<br />
                  でも、胸が苦しくなる。
                </p>
              </div>
            </div>

            {/* ⑥ 年収1,000万円なのに、苦しい */}
            <div className="text-center max-w-4xl mx-auto bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="mb-8">
                <div className="relative w-full max-w-xl mx-auto" style={{ paddingBottom: '56.25%' }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=Emotional%20illustration%20of%20young%20Japanese%20child%20talking%20to%20father%20about%20wanting%20family%20vacation%20in%20modern%20living%20room%2C%20parents%20looking%20concerned%20at%20travel%20brochures%20and%20budget%20calculations%2C%20touching%20family%20moment%20depicted%20in%20contemporary%20minimalist%20style%20with%20unified%20warm%20color%20scheme%20of%20soft%20beige%20cream%20light%20gray%20and%20gentle%20neutral%20tones%2C%20natural%20indoor%20lighting%20creating%20intimate%20domestic%20atmosphere&width=800&height=450&seq=family-vacation-talk-unified-002&orientation=landscape"
                    alt="家族旅行の相談"
                    className="absolute inset-0 w-full h-full object-cover object-top rounded-lg shadow-md"
                  />
                </div>
              </div>
              <div className="text-left space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed">
                <p>
                  ある日、子どもに言われた。
                </p>
                <p>
                  「〇〇くんの家、旅行行ってるよ」<br />
                  「うちも行きたい」
                </p>
                <p>
                  旅行の見積もりを取ると、<br />
                  家族5人で 約100万円。
                </p>
                <p>
                  妻は言った。
                </p>
                <p>
                  「そんなの無理でしょ」
                </p>
                <p>
                  家の空気が、重くなる。
                </p>
                <p>
                  そのとき、頭に浮かんだ。
                </p>
                <p className="text-2xl font-bold text-red-600">
                  「俺、年収1,000万円あるよな?」<br />
                </p>
                <p>
                  原因がひとつだとは思わない。<br />
                  でも、家計を見直して気づいた。
                </p>
                <p>
                  毎月当たり前に出ていく固定費の中で、<br />
                  住宅費は一番影響が大きく、かといって下げることもできない。<br />
                  でも住宅費が軽くなったらどうだろう?
                </p>
                <p>
                  もし、ここが少しでも軽くなったら、家計は確実に変わる。
                </p>
                <p className="font-bold text-gray-900">
                  そう思った。
                </p>
              </div>
            </div>

            {/* ⑦ たまたま知った、ひとつの考え方 */}
            <div className="text-center max-w-4xl mx-auto bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="mb-8">
                <div className="relative w-full max-w-xl mx-auto" style={{ paddingBottom: '56.25%' }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=Hopeful%20illustration%20of%20Japanese%20couple%20having%20serious%20discussion%20about%20financial%20solution%20at%20dining%20table%20with%20laptop%20showing%20housing%20cost%20reduction%20information%2C%20expressions%20of%20interest%20and%20cautious%20optimism%2C%20warm%20modern%20home%20interior%20rendered%20in%20cohesive%20minimalist%20style%20with%20harmonious%20palette%20of%20soft%20beige%20cream%20gentle%20gray%20and%20warm%20neutral%20tones%2C%20evening%20ambient%20lighting%20creating%20intimate%20atmosphere&width=800&height=450&seq=couple-discussion-unified-002&orientation=landscape"
                    alt="ノウハウの発見"
                    className="absolute inset-0 w-full h-full object-cover object-top rounded-lg shadow-md"
                  />
                </div>
              </div>
              <div className="text-left space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed">
                <p>
                  そんなとき、<br />
                  たまたま【家賃ゼロ太郎くん】を知った。
                </p>
                <p>
                  「住宅費が無料になる？<br />
                  ……怪しいな。」
                </p>
                <p>
                  正直、そう思った。<br />
                  でも同時に、<br />
                  「……少し魅力的だな」とも感じた。
                </p>
                <p>
                  そのときは、<br />
                  深く考えずにスルーした。
                </p>
                <p>
                  それからしばらくして、<br />
                  子どものお受験をきっかけに、<br />
                  知り合いから再び家賃ゼロ太郎君の話を聞いた。
                </p>
                <p>
                  「あれ？<br />
                  これ、前にも見たな。」
                </p>
                <p>
                  そう思って、<br />
                  知り合いを通じてセミナーに参加してみた。
                </p>
                <p>
                  話を聞いてみると、<br />
                  思っていたほど難しくない。<br />
                  私でも、きちんと理解できる内容だった。
                </p>
              </div>
            </div>

            {/* ⑨ 翌年、本当に変化があった - 統合版 */}
            <div className="text-center max-w-4xl mx-auto bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <div className="mb-8">
                <div className="relative w-full max-w-xl mx-auto" style={{ paddingBottom: '56.25%' }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=Celebratory%20illustration%20of%20Japanese%20man%20looking%20at%20bank%20statement%20showing%20increased%20savings%20of%202.4%20million%20yen%20on%20computer%20screen%2C%20expression%20of%20pleasant%20surprise%20and%20relief%2C%20modern%20home%20office%20setting%20rendered%20in%20unified%20minimalist%20style%20with%20warm%20harmonious%20color%20scheme%20of%20soft%20beige%20cream%20light%20gray%20and%20gentle%20neutral%20tones%2C%20natural%20lighting%20creating%20positive%20uplifting%20atmosphere&width=800&height=450&seq=savings-success-unified-002&orientation=landscape"
                    alt="貯金が増えた"
                    className="absolute inset-0 w-full h-full object-cover object-top rounded-lg shadow-md"
                  />
                </div>
              </div>
              <div className="text-left space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed">
                <p>
                  正直、半信半疑だった。
                </p>
                <p>
                  でも、言われた通りに行動した。<br />
                  すると翌年、<br />
                  約240万円の余裕が生まれていた。
                </p>
                <p className="font-bold text-gray-900">
                  月に1万円の余裕でも、世界は変わる。それが月に20万円だ。
                </p>
                <p>
                  外食の回数が増える。
                </p>
                <p>
                  東京へ引っ越しができ通勤のストレスも減った。
                </p>
                <p>
                  仕事にも余裕が生まれ、<br />
                  子どもとの時間も増えた。
                </p>
                <p>
                  家族の笑顔も増えた。
                </p>
                <p>
                  最近では、<br />
                  同僚や部下から言われる。
                </p>
                <p>
                  「なんか前と違いますよね」<br />
                  「余裕ありますよね」
                </p>
                <p className="font-bold text-gray-900">
                  自分でも、そう思う。
                </p>
              </div>
            </div>

            {/* ⑩ これは「誰かの成功談」ではなく、私の告白です */}
            <div className="text-center max-w-4xl mx-auto bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-10 shadow-lg border-2 border-blue-200">
              <div className="text-left space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed">
                <p className="font-bold text-gray-900">
                  これは、<br />
                  どこかの誰かの事例ではありません。
                </p>
                <p className="text-2xl font-bold text-blue-600">
                  私自身の、事例です。
                </p>
                <p>
                  特別な才能があったわけでも、<br />
                  もともとお金に詳しかったわけでもありません。
                </p>
                <p>
                  たまたま【家賃ゼロ太郎くん】を知った。そして使った。
                </p>
              </div>
            </div>

            {/* ⑪ 私は、このノウハウを「作った人」ではありません */}
            <div className="text-center max-w-4xl mx-auto bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
                誤解してほしくないのですが、<br />
                私はこのノウハウを作った人間ではありません。
              </h3>
              <div className="text-left space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed">
                <p>
                  どこかで大きなセミナーをやっていたり、<br />
                  表に出ている講師だったりするわけでもない。
                </p>
                <p className="font-bold text-gray-900">
                  私は、ただ"利用した者"です。
                </p>
                <p>
                  あるきっかけでこの考え方を知り、<br />
                  自分の生活で試してみただけ。
                </p>
                <p>
                  そして、<br />
                  「これは本当に使える」 と感じました。
                </p>
                <p className="text-3xl md:text-4xl font-bold text-gray-900">
                  同じような境遇の人にも知ってほしいと思っています。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}